import React from "react"
import { t } from "@/i18n"
import compose from "recompose/compose"
import { withTracking } from "@/helpers/hoc/withTracking"
import injectSheet from "react-jss"
import { Carousel } from "@xingternal/360-components"
import { FencingTitle, ProductBrochure } from "@/components"
import { getProduct } from "@/helpers/products"
import CarouselNav from "./CarouselNav"
import CarouselDots from "./CarouselDots"
import xtmImg from "./images/xtm.svg"
import xtpImg from "./images/xtp.svg"
import xjmImg from "./images/xjm.svg"
import ebpImg from "./images/ebp.svg"
import eRecruitingImg from "./images/360.svg"
import { withPermissions } from "@/helpers/hoc/withPermissions"
import styled from "styled-components"
import { themeGet } from "@styled-system/theme-get"
import { Box } from "@xingternal/winery-ui/lib/Box"

const FencingBox = styled(Box)`
  padding-left: ${themeGet("space.7")};
  padding-right: ${themeGet("space.9")};
  margin-top: ${themeGet("space.4")};
  margin-bottom: ${themeGet("space.5")};
`

const fencingSlideStyles = {
  title: {
    composes: "mb24 pl40 pr64 mt16",
  },
  content: {
    composes: "px40",
  },
}

const slides = () => [
  {
    title: t("360_RECRUITER_INSIGHTS_FENCING_LIGHTBOX_360_HEADLINE"),
    intro: t("360_RECRUITER_INSIGHTS_FENCING_LIGHTBOX_360_DESCRIPTION"),
    moreInfo: t("360_RECRUITER_INSIGHTS_FENCING_LIGHTBOX_DISCOVER_360"),
    marketingUrl: t("360_RECRUITER_INSIGHTS_FENCING_LINK_360"),
    product: "360",
    image: <img src={eRecruitingImg} />,
    listItems: [
      t("360_RECRUITER_INSIGHTS_FENCING_LIGHTBOX_360_BENEFIT01"),
      t("360_RECRUITER_INSIGHTS_FENCING_LIGHTBOX_360_BENEFIT02"),
      t("360_RECRUITER_INSIGHTS_FENCING_LIGHTBOX_360_BENEFIT03"),
    ],
  },
  {
    title: t("360_RECRUITER_INSIGHTS_FENCING_LIGHTBOX_XTM_HEADLINE"),
    intro: t("360_RECRUITER_INSIGHTS_FENCING_LIGHTBOX_XTM_DESCRIPTION"),
    moreInfo: t("360_RECRUITER_INSIGHTS_FENCING_LIGHTBOX_DISCOVER", {
      product: getProduct("xtm").title,
    }),
    marketingUrl: t("360_RECRUITER_INSIGHTS_FENCING_LINK_XTM"),
    product: "xtm",
    image: <img src={xtmImg} />,
    listItems: [
      t("360_RECRUITER_INSIGHTS_FENCING_LIGHTBOX_XTM_BENEFIT01"),
      t("360_RECRUITER_INSIGHTS_FENCING_LIGHTBOX_XTM_BENEFIT02"),
      t("360_RECRUITER_INSIGHTS_FENCING_LIGHTBOX_XTM_BENEFIT03"),
    ],
  },
  {
    title: t("360_RECRUITER_INSIGHTS_FENCING_LIGHTBOX_XTP_HEADLINE"),
    intro: t("360_RECRUITER_INSIGHTS_FENCING_LIGHTBOX_XTP_DESCRIPTION"),
    moreInfo: t("360_RECRUITER_INSIGHTS_FENCING_LIGHTBOX_DISCOVER", {
      product: getProduct("xtp").title,
    }),
    marketingUrl: t("360_RECRUITER_INSIGHTS_FENCING_LINK_XTP"),
    product: "xtp",
    image: <img src={xtpImg} />,
    listItems: [
      t("360_RECRUITER_INSIGHTS_FENCING_LIGHTBOX_XTP_BENEFIT01"),
      t("360_RECRUITER_INSIGHTS_FENCING_LIGHTBOX_XTP_BENEFIT02"),
      t("360_RECRUITER_INSIGHTS_FENCING_LIGHTBOX_XTP_BENEFIT03"),
    ],
  },
  {
    title: t("360_RECRUITER_INSIGHTS_FENCING_LIGHTBOX_XJM_HEADLINE"),
    intro: t("360_RECRUITER_INSIGHTS_FENCING_LIGHTBOX_XJM_DESCRIPTION"),
    moreInfo: t("360_RECRUITER_INSIGHTS_FENCING_LIGHTBOX_DISCOVER_XJM"),
    marketingUrl: t("360_RECRUITER_INSIGHTS_FENCING_LINK_XJM"),
    product: "xjm",
    image: <img src={xjmImg} />,
    listItems: [
      t("360_RECRUITER_INSIGHTS_FENCING_LIGHTBOX_XJM_BENEFIT01"),
      t("360_RECRUITER_INSIGHTS_FENCING_LIGHTBOX_XJM_BENEFIT02"),
      t("360_RECRUITER_INSIGHTS_FENCING_LIGHTBOX_XJM_BENEFIT03"),
    ],
  },
  {
    title: t("360_RECRUITER_INSIGHTS_FENCING_LIGHTBOX_EBP_HEADLINE"),
    intro: t("360_RECRUITER_INSIGHTS_FENCING_LIGHTBOX_EBP_DESCRIPTION"),
    moreInfo: t("360_RECRUITER_INSIGHTS_FENCING_LIGHTBOX_DISCOVER_EBP"),
    marketingUrl: t("360_RECRUITER_INSIGHTS_FENCING_LINK_EBP"),
    product: "ebp",
    image: <img src={ebpImg} />,
    listItems: [
      t("360_RECRUITER_INSIGHTS_FENCING_LIGHTBOX_EBP_BENEFIT01"),
      t("360_RECRUITER_INSIGHTS_FENCING_LIGHTBOX_EBP_BENEFIT02"),
      t("360_RECRUITER_INSIGHTS_FENCING_LIGHTBOX_EBP_BENEFIT03"),
    ],
  },
]

const FencingSlide = injectSheet(fencingSlideStyles)(
  ({ classes, product, ...rest }) => (
    <React.Fragment>
      <FencingBox>
        <FencingTitle product={product} reversed className={classes.title} />
      </FencingBox>
      <ProductBrochure
        className={classes.content}
        product={product}
        {...rest}
      />
    </React.Fragment>
  ),
)

const onMoreInfo = (marketingUrl, trackAction) => () => {
  window.open(marketingUrl)
  trackAction("xri_open_xing360_click")
}

const showNotice = ({ availableProducts, permissions, product }) =>
  availableProducts[product] && !permissions.products[product]

const FencingCarousel = ({
  initialSlide,
  trackAction,
  availableProducts,
  permissions,
}) => (
  <Carousel
    widgets={[CarouselNav, CarouselDots]}
    loop
    initialSlide={initialSlide < 5 ? initialSlide : 0}
  >
    {slides().map(
      ({ title, intro, marketingUrl, moreInfo, product, image, listItems }) => (
        <FencingSlide
          key={product}
          title={title}
          intro={intro}
          listItems={listItems}
          moreInfoLabel={moreInfo}
          onMoreInfo={onMoreInfo(marketingUrl, trackAction)}
          product={product}
          showAvailableProductNotice={showNotice({
            availableProducts,
            permissions,
            product,
          })}
          image={image}
        />
      ),
    )}
  </Carousel>
)

const enhance = compose(withPermissions, withTracking())

export const EnhancedFencingCarousel = enhance(FencingCarousel)
export default FencingCarousel
